import { useState } from 'react';
import { X, Save, Loader2, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import type { StockInDTO, StockOutDTO, StockAdjustmentDTO, StockMovementType } from '@yawi/types';
import { useI18n } from '@/shared/i18n';

interface StockMovementFormProps {
  consumableName: string;
  currentStock: number;
  movementType: 'IN' | 'OUT' | 'ADJUSTMENT';
  onSubmit: (data: StockInDTO | StockOutDTO | StockAdjustmentDTO) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const StockMovementForm = ({
  consumableName,
  currentStock,
  movementType,
  onSubmit,
  onCancel,
  loading,
}: StockMovementFormProps) => {
  const { t } = useI18n();

  const [quantity, setQuantity] = useState<number>(0);
  const [newStock, setNewStock] = useState<number>(currentStock);
  const [reason, setReason] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;

    if (movementType === 'ADJUSTMENT') {
      await onSubmit({ newStock, reason } as StockAdjustmentDTO);
    } else {
      await onSubmit({
        quantity,
        reason,
        referenceNumber: referenceNumber || undefined,
      } as StockInDTO | StockOutDTO);
    }
  };

  const getTitle = () => {
    switch (movementType) {
      case 'IN':
        return t('inventory.stockIn');
      case 'OUT':
        return t('inventory.stockOut');
      case 'ADJUSTMENT':
        return t('inventory.stockAdjustment');
    }
  };

  const getIcon = () => {
    switch (movementType) {
      case 'IN':
        return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'OUT':
        return <TrendingDown className="w-5 h-5 text-slate-600" />;
      case 'ADJUSTMENT':
        return <RefreshCw className="w-5 h-5 text-blue-600" />;
    }
  };

  const getButtonColor = () => {
    switch (movementType) {
      case 'IN':
        return 'bg-green-600 hover:bg-green-700';
      case 'OUT':
        return 'bg-slate-600 hover:bg-slate-700';
      case 'ADJUSTMENT':
        return 'bg-blue-600 hover:bg-blue-700';
    }
  };

  const projectedStock = movementType === 'ADJUSTMENT'
    ? newStock
    : movementType === 'IN'
      ? currentStock + quantity
      : currentStock - quantity;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            {getIcon()}
            <div>
              <h2 className="text-lg font-semibold text-slate-800">{getTitle()}</h2>
              <p className="text-sm text-slate-500">{consumableName}</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Current Stock Display */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">{t('inventory.currentStock')}:</span>
              <span className="text-lg font-bold text-slate-800">{currentStock}</span>
            </div>
          </div>

          {/* Quantity or New Stock */}
          {movementType === 'ADJUSTMENT' ? (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('inventory.newStock')} *
              </label>
              <input
                type="number"
                value={newStock}
                onChange={(e) => setNewStock(Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg font-mono"
                min="0"
                required
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('inventory.quantity')} *
              </label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg font-mono"
                min="1"
                max={movementType === 'OUT' ? currentStock : undefined}
                required
              />
            </div>
          )}

          {/* Projected Stock */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">{t('inventory.projectedStock')}:</span>
              <span className={`text-lg font-bold ${projectedStock < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                {projectedStock}
              </span>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('inventory.reason')} *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              rows={2}
              required
              placeholder={t('inventory.reasonPlaceholder')}
            />
          </div>

          {/* Reference Number (only for IN/OUT) */}
          {movementType !== 'ADJUSTMENT' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('inventory.referenceNumber')}
              </label>
              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('inventory.referencePlaceholder')}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !reason.trim() || (movementType !== 'ADJUSTMENT' && quantity <= 0) || projectedStock < 0}
              className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${getButtonColor()}`}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {t('common.confirm')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
